import { Popover } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { Icon } from "~/assets/icons/Icon";
import { routes } from "~/router";

import { Site } from "../../api/Api";
import { DomainStatus } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { formatNumber } from "../../helpers/helpers";
import FormLabel from "../login/formLabel/FormLabel";
import Button from "../mantine/button/Button";
import NotificationBlock from "../notificationBlock/NotificationBlock";
import Input from "../ui/input/Input";
import Modal from "../ui/modal/Modal";
import styles from "./SiteCard.module.scss";

type SiteWithStats = Site & {
  stats: Stats;
  isStatsLoading?: boolean;
};
interface SiteCardProps {
  site: SiteWithStats | null;
  sitesIsPenging?: boolean;
  canDelete?: boolean;
}
export type Stats = {
  keywords: {
    total: number;
    top_10: number;
    top_10_percent: number;
    top_25: number;
    top_25_percent: number;
    life: number;
    life_percent: number;
  };
  recommendations: {
    total: number;
    active: number;
  };
  pages: {
    total: number;
    optimized: number;
  };
};
const groupedStats = (stats?: Stats) => [
  {
    "Top 10": {
      total: stats?.keywords?.total,
      count: stats?.keywords?.top_10,
      percent: stats?.keywords?.top_10_percent,
    },
  },
  {
    "Top 25": {
      total: stats?.keywords?.total,
      count: stats?.keywords?.top_25,
      percent: stats?.keywords?.top_25_percent,
    },
  },
  {
    "Lifetime Up": {
      count: stats?.keywords?.life,
      total: stats?.keywords?.total,
      percent: stats?.keywords?.life_percent,
    },
  },
  {
    Recommendations: {
      total: stats?.recommendations?.total,
      count: stats?.recommendations?.active,
    },
  },
  {
    "Pages Optimized": {
      total: stats?.pages?.total,
      count: stats?.pages?.optimized,
    },
  },
];
export const SiteCard = ({
  site,
  sitesIsPenging,
  canDelete,
}: SiteCardProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { register, watch } = useForm({
    mode: "onChange",
    defaultValues: {
      confirmDomain: "",
    },
  });
  const confirmDomain = watch("confirmDomain");
  const isConfirmed = confirmDomain === site?.fqdn;
  const { mutate: deleteSite } = useMutation({
    mutationFn: (id: number) => apiClient.sites.sitesDelete(id),

    onError: (error) => {
      //@ts-expect-error be responce
      const errorMessage = error?.error?.error?.message || "Error";
      enqueueSnackbar(errorMessage, {
        variant: "error",
        persist: true,
      });
    },
    onSuccess: () => {
      enqueueSnackbar("Site was successfully deleted", {
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["dashboard"],
      });
    },
  });
  const siteFavicon =
    typeof site?.favicon === "object" ? (
      <img src={site?.favicon?.url} className={styles.titleFaviconImg} />
    ) : (
      <Icon
        iconName="SiteFaviconPlaceholder"
        width="1rem"
        height="1rem"
        // @ts-expect-error TS2339 non-existing className
        className={styles.sidebarTitleIcon}
      />
    );

  const handleDeleteWebsite = () => {
    if (isConfirmed) {
      deleteSite(site?.id);
      setIsDeleteModalOpen(false);
    }
  };

  const status = site?.delegated_domain?.status;

  const siteSpeedError = (
    Object.keys(status || {}) as (keyof DomainStatus)[]
  ).find((k) => status?.[k] === false);

  return (
    <>
      <article className={styles.card}>
        <header className={styles.header}>
          {sitesIsPenging ? (
            <Skeleton count={1} className={styles.skeleton} />
          ) : (
            <div className={styles.domain}>
              {site && (
                <h3 className={styles.domainTitle}>
                  <Link
                    className={styles.domainTitleLink}
                    to={routes.site.root({ id: site.id })}
                  >
                    {siteFavicon}
                    {site?.fqdn}
                  </Link>
                </h3>
              )}

              {
                //   AI Visibility Engine icon should only be visible if AI Visibility Engine is enabled
              }

              {site?.prerendering &&
                (siteSpeedError ? (
                  <span
                    className={styles.status}
                    key={site.delegated_domain?.id}
                    data-tooltip-id={`site-speed-error-${site.id}`}
                  >
                    <Icon
                      iconName="SiteSpeedErrIcon"
                      width="1.75rem"
                      height="1.75rem"
                    />
                  </span>
                ) : (
                  <span
                    className={styles.status}
                    key={site.delegated_domain?.id}
                    data-tooltip-id={`site-speed-${site.id}`}
                  >
                    <Icon
                      iconName="SiteSpeedIcon"
                      width="1.75rem"
                      height="1.75rem"
                    />
                  </span>
                ))}

              {
                //   Do not display the error icon until the first crawl is complete
              }

              {site?.widget_installed ? (
                <span
                  className={styles.status}
                  data-tooltip-id={`site-widget-${site?.id}`}
                >
                  <Icon
                    iconName="SnippetIcon"
                    width="1.75rem"
                    height="1.75rem"
                  />
                </span>
              ) : (
                site?.crawled_at && (
                  <span
                    className={styles.status}
                    data-tooltip-id={`site-widget-error-${site?.id}`}
                  >
                    <Icon
                      iconName="SnippetErrIcon"
                      width="1.75rem"
                      height="1.75rem"
                    />
                  </span>
                )
              )}
            </div>
          )}
          <div className={styles.meta}>
            {site && (
              <Button
                variant="outlined"
                disabled={sitesIsPenging}
                onClick={() => navigate(routes.site.root({ id: site.id }))}
              >
                View Project
              </Button>
            )}

            <div className={styles.actionButton}>
              <Popover
                offset={1}
                position="bottom"
                withinPortal={true}
                opened={isPopoverOpen}
                onChange={setIsPopoverOpen}
                zIndex={1000}
                styles={{
                  dropdown: {
                    minWidth: "150px",
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant="unstyled"
                    onClick={() => setIsPopoverOpen((o) => !o)}
                  >
                    <Icon
                      iconName="TreeDotsButtonIcon"
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                </Popover.Target>
                <Popover.Dropdown className={styles.dropdown}>
                  {site && (
                    <Button
                      variant="menuItem"
                      className={styles.popoverItem}
                      component={Link}
                      to={routes.site.settings.root({ id: site.id })}
                    >
                      Site Settings
                    </Button>
                  )}

                  <Button
                    disabled={!canDelete}
                    variant="menuItem"
                    className={styles.popoverItemRed}
                    onClick={() => {
                      setIsPopoverOpen(false);
                      setIsDeleteModalOpen(true);
                    }}
                  >
                    <span className={styles.popoverItemRed}>Delete Site</span>
                  </Button>
                </Popover.Dropdown>
              </Popover>
            </div>
          </div>
        </header>

        {site?.prerendering && (
          <Tooltip
            id={`site-speed-${site?.id}`}
            content="AI Visibility Engine enabled and configured properly"
            place="top"
            style={{ borderRadius: "var(--border-radius)" }}
          />
        )}

        {site?.prerendering && (
          <Tooltip
            id={`site-speed-error-${site?.id}`}
            content="AI Visibility Engine configuration incomplete"
            place="top"
            style={{ borderRadius: "var(--border-radius)" }}
          />
        )}

        {site?.widget_installed && (
          <Tooltip
            id={`site-widget-${site?.id}`}
            content="The JS widget is installed"
            place="top"
            style={{ borderRadius: "var(--border-radius)" }}
          />
        )}

        {site?.crawled_at && (
          <Tooltip
            id={`site-widget-error-${site?.id}`}
            content="The JS widget is not installed"
            place="top"
            style={{ borderRadius: "var(--border-radius)" }}
          />
        )}

        <div className={styles.stats}>
          {!site?.isStatsLoading && !sitesIsPenging
            ? groupedStats(site?.stats).map((group) => {
                const [key, value] = Object.entries(group)[0];

                return (
                  <div key={key} className={styles.statGroup}>
                    <h4>{key}</h4>
                    <div className={styles.statValue}>
                      {/*
                       // @ts-expect-error TS2551 non-existing className */}
                      <span className={styles.statFullValue}>
                        <span className={styles.statValueCount}>
                          {formatNumber(value.count)}
                        </span>
                        /
                        <span className={styles.statValueTotal}>
                          {formatNumber(value.total)}
                        </span>
                      </span>
                      {value.percent > 0 && (
                        <div className={styles.percentUp}>
                          +{formatNumber(Math.ceil(value.percent))}%
                          <Icon iconName="PercentUpIcon" />
                        </div>
                      )}
                      {value.percent < 0 && (
                        <div className={styles.percentDown}>
                          -{formatNumber(Math.ceil(Math.abs(value.percent)))}%
                          <Icon iconName="PercentDownIcon" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            : groupedStats().map((item, index) => {
                const [key] = Object.entries(item)[0];

                return (
                  <div className={styles.statGroup} key={index}>
                    <h4>{key}</h4>
                    <Skeleton count={1} className={styles.skeleton} />
                  </div>
                );
              })}
        </div>
      </article>
      <Modal
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        modalLabel={
          // @ts-expect-error TS2339 non-existing className
          <h3 className={styles.modalTitle}>
            {/*
             // @ts-expect-error TS2339 non-existing className */}
            <div className={styles.dangerIconWrapper}>
              <Icon iconName="DangerIcon" width="1rem" height="1rem" />
            </div>
            Delete Site
          </h3>
        }
        buttonLabel="Delete"
        action={handleDeleteWebsite}
        actionButtonColor="danger"
        isDisabled={!isConfirmed}
      >
        {/*
         // @ts-expect-error TS2339 non-existing className */}
        <div className={styles.deleteConfirmation}>
          <p>
            Seems like you are going to delete your{" "}
            <strong>{site?.fqdn}</strong> from your team. These will lead to the
            permanent loss of the website information{" "}
          </p>
        </div>
        <p>
          <NotificationBlock
            type="warning"
            message="Pay attention! Action cannot be reversed. If you are using site speed optimization tool don`t forget to transfer your website domain to different hosting, or it might disrupt the work of your website"
          />
        </p>
        <p>
          <FormLabel label="Confirm Deletion" />
          <Input
            id="delete-confirmation"
            {...register("confirmDomain", {
              validate: (value) => value === site?.fqdn,
            })}
            placeholder={`Type ${site?.fqdn} to confirm`}
          />
        </p>
      </Modal>
    </>
  );
};

export default SiteCard;
